import { Injectable, Inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/internal/Observable';
import { getValidAuthToken, AuthState, AuthTokenData } from '../ngrx/state/auth.state';
import { map, take } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { SetAuthToken, LogoutAction, SetAuthTokenData } from '../ngrx/action/auth.action';
import { getAuthenticationConfig } from './authentication.config';
import { APP_CONFIG } from '../../tokens/app-config.token';
import { Environment } from '../../../environments/environment.interface';
import jwt_decode from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  private readonly authConfig: ReturnType<typeof getAuthenticationConfig>;

  constructor(
    private readonly authStore: Store<AuthState>,
    private readonly cookieService: CookieService,
    @Inject(APP_CONFIG) private readonly appConfig: Environment,
  ) {
    this.authConfig = getAuthenticationConfig(this.appConfig);
  }

  /**
   * Check if the user is authenticated
   */
  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    this.setAuthStoreData();

    return this.authStore.select(getValidAuthToken).pipe(
      take(1), // take only one to end the observable
      map((authToken) => {
        if (!authToken) {
          this.authStore.dispatch(LogoutAction());
          return false;
        }
          return true;
      }),
    );
  }

  private setAuthStoreData() {
    try {
      const authToken = this.getAuthTokenFromCookie();
      const decodedToken: AuthTokenData = jwt_decode(authToken);

      this.authStore.dispatch(SetAuthToken({ authToken }));
      this.authStore.dispatch(SetAuthTokenData({ authTokenData: decodedToken }));
    } catch (e) {
      this.authStore.dispatch(LogoutAction());
    }
  }

  private getAuthTokenFromCookie() {
    return this.cookieService.get(this.authConfig.authTokenKeyName);
  }
}
